import type { Product } from '@/types';
import { FramedImage } from '@/components/FramedImage';

/** The covers a collage needs: component id, name (alt) and photo. */
export type CollageItem = Pick<Product, 'id' | 'name' | 'image'>;

/**
 * Automatic photo of a pack without its own picture (docs/plan-packs.md): the
 * components' covers in a 2×2 grid. Fills its positioned parent, which owns the
 * 4:5 frame (`.card__media`, `.pdp__gallery`), like FramedImage. 1 component →
 * one cover; 2 → two columns; 3 → one tall cover + two; 4+ → the first four.
 * Decorative by default (`alt` on the wrapper names the pack).
 */
export function PackCollage({
  items,
  alt,
  loading,
  sizes,
}: {
  items: readonly CollageItem[];
  /** Accessible name of the whole collage (the pack name); empty = decorative. */
  alt: string;
  loading?: 'lazy' | 'eager';
  sizes?: string;
}) {
  const cells = items.slice(0, 4);
  const n = Math.max(1, cells.length);
  return (
    <span
      className={`pack-collage pack-collage--${n}`}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
    >
      {cells.map((c) => (
        <span key={c.id} className="pack-collage__cell">
          {c.image ? (
            <FramedImage src={c.image} alt="" loading={loading} sizes={sizes} fallback="CARO" />
          ) : (
            <span className="card__placeholder" aria-hidden>
              CARO
            </span>
          )}
        </span>
      ))}
    </span>
  );
}
